import { IBuildingsHasPhotos, IListingPhotos } from '@llws/lift-entity-interfaces';
import { IApiCommandResponse } from '@llws/api-common';
import { IQueryCommandOptions, Equal } from '@llws/dynamic-router-utils';
import { IApiResponse, IModifyPayload, subsetObject } from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import uploads from '@/shared/services/uploads';
import { getNewListingPhoto } from '@/shared/factory/new-listing-photo';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { getNewBuildingsHasPhotos } from '@/shared/factory/new-buildings-has-photos';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/properties/:param/property-has-photos';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  relations: [{ path: 'listingPhoto', alias: 'lp' }],
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
  where: [Equal('lp.active', '1')],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IBuildingsHasPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'listing-photos/'

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,

    uploadAndSave: async (
      ctx: IAction<IStateStrict<IBuildingsHasPhotos>>,
      payload: IModifyPayload<number, File, { orderBy: number, clientKey: string, clientId: number }>,
    ): Promise<IBuildingsHasPhotos> => {
      const uploadResult = await uploads.uploadSingleFile(
        payload.additional!.clientKey,
        'gallery',
        payload.data,
      );

      const image = uploadResult.filename.split('/').pop()!;

      const listingPhotoResponse: IApiResponse<IListingPhotos[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/listing-photos`,
        getNewListingPhoto(payload.additional!.clientId, payload.parentId!, image, uploadResult.height, uploadResult.width),
      );

      const buildingsHasPhotosResponse: IApiResponse<IBuildingsHasPhotos[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/property-has-photos`,
        subsetObject(getNewBuildingsHasPhotos(listingPhotoResponse.data![0]!, payload.additional!.orderBy), ['listingPhoto']),
      );

      const buildingsHasPhoto = { ...buildingsHasPhotosResponse.data![0]!, listingPhoto: listingPhotoResponse.data![0]! };
      ctx.commit('set', [...ctx.getters.list, buildingsHasPhoto ]);
      return buildingsHasPhoto;
    },

    removeMany: async (
      ctx: IAction<IStateStrict<IBuildingsHasPhotos>>,
      payload: IModifyPayload<number, IBuildingsHasPhotos[]>,
    ): Promise<IBuildingsHasPhotos[]> => {
      const result: IApiCommandResponse<IBuildingsHasPhotos[] | undefined> = await adminRequest
        .delete(adminGatewayResourcePath.replace(':param', `${payload.parentId!}`), payload.data.map((v) => v.id!));

      ctx.commit('set', ctx.getters.list
      // @ts-ignore
        .filter((v: IBuildingsHasPhotos) => !result.data?.some((bhp) => bhp.listingPhotoId === v.listingPhotoId)));
      return payload.data!;
    },
  },
};
